// Pure decision helpers for the warm-resume subscription-lockout policy. Kept
// free of any Angular / Sentry imports so they can be unit-tested standalone.
// app.component.ts (postLoginWarmResumeAsync / warmResumePhaseB) delegates its
// two lockout decisions here.
//
// Background: on a warm resume the app renders Phase A from the locally cached
// role before it has logged in to the server, then logs in to the server in the
// background in Phase B. Without these two rules, a user whose cache says
// "expired" but who has since paid (their renewal already processed
// server-side) would be shown the lockout from a stale cache and would stay
// stranded on the locked welcome page even after the background login learned
// they are paid.

import { AuthRole } from '@cf-shared/couch-auth';

// Whether, on warm resume, we must recheck the subscription with the server
// BEFORE showing the lockout. It is fine to trust a cached "subscribed" role,
// but a cached "expired/unpaid" must not lock the user out until the server has
// been consulted — a renewal may have processed while the cache is stale.
//
// Only the already-onboarded ("welcome phase done") cohort is gated this way: a
// user who has not finished onboarding is legitimately on welcome pages
// regardless of their role, so there is nothing to recheck for them.
export function shouldRecheckSubscriptionBeforeLockout(params: {
  welcomePhaseDone: boolean;
  cachedRole: AuthRole;
}): boolean {
  return params.welcomePhaseDone && params.cachedRole < AuthRole.Paid;
}

// Whether, after the warm-resume background server login refreshes the role, we
// must route the user into the app. This is true exactly when the user was
// locked out from the stale cache but the fresh server role has now unlocked
// the app — nothing else re-evaluates the lockout guard after that background
// login, so without this the now-paid user would stay on the locked page.
export function shouldRouteInAfterServerLogin(params: {
  wasLockedBeforeLogin: boolean;
  unlockedAfterLogin: boolean;
}): boolean {
  return params.wasLockedBeforeLogin && params.unlockedAfterLogin;
}
